import { Component, Inject, Input, OnInit, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { NodeType } from '../../../classes/tree/IBaseNode';
import { ECSNode } from '../../../classes/tree/nodes/ECSNode';
import { Tree } from '../../../classes/tree/tree';
import { DocumentService } from '../../../services/document.service';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { LogLevel, MapCoreDocument } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
  encapsulation: ViewEncapsulation.ShadowDom,
  selector: 'mc-tree-explorer-ng',
  templateUrl: './tree-explorer.component.html',
  styleUrls: ['../../sharedStyles/base.scss', './tree-explorer.component.scss']
})
export class TreeExplorerComponent extends BaseComponent implements OnInit {

  NodeType = NodeType;

  @Input()
  public buildGroups: (node: Tree<any>, key: string) => [string, any][];

  currentNode: Tree<any>;
  public asc: boolean = true;
  public groups: any;
  public loading: boolean;
  public orderBy: string;

  private openedDropdown: any;

  _documents: MapCoreDocument[];

  @Input()
  set documents(value: MapCoreDocument[]) {
    this._documents = value.filter(d => !this.documentService.isImage(d) && !this.documentService.isVideo(d));
    this.updateDocSources();
  }

  get documents(): MapCoreDocument[] {
    return this._documents;
  }

  constructor(vcr: ViewContainerRef,
              private readonly documentService: DocumentService,
              @Inject('LoggingService') private readonly loggingService: ILoggingService) {
    super(vcr);
  }

  ngOnInit() {}

  public navigateToNode(node: Tree<any>) {
    try {
      this.loading = false;

      if (node.children.length > 0) {
        this.currentNode = node;

        // set this.asc and this.orderby
        this.asc = node.data.asc || true;
        if (node.data.orderBy) {
          this.orderBy = node.data.orderBy;
        }

        this.sortChildren(node, this.orderBy, this.asc);
      } else {
        this.currentNode = node.parent; // Didn't find data, return user to parent node
      }

      return this.currentNode;
    } catch (error) {
      this.loggingService.Log(error, LogLevel.Error);
    }
  }

  async updateDocSources() {
    if (this.documents && this.documents.length > 0) {
      const rootNode = new ECSNode();
      rootNode.name = 'Documents';
      rootNode.description = 'Documents';
      rootNode.orderBy = 'documentType';

      const tree = new Tree<ECSNode>(rootNode);
      tree.children = this.documents.map(d => new Tree<ECSNode>(new ECSNode(d)));

      const names = Object.keys(tree.children[0].data.record);
      tree.data.columns = names.map(name => {
        return { name, alias: this.camelCaseToAlias(name) };
      });

      this.navigateToNode(tree);
    }
  }

  camelCaseToAlias(columnName: string) {
    let lastPart = 0;
    const parts: string[] = [];

    for (let i = 1; i <= columnName.length; i++) {
      if (i === columnName.length || (columnName[i] >= 'A' && columnName[i] <= 'Z')) {
        parts.push(columnName.substring(lastPart, i));
        lastPart = i;
      }
    }

    // Capitalize the first part
    parts[0] = parts[0][0].toUpperCase() + parts[0].substring(1);

    return parts.join(' ');
  }

  public async onMouseEnter() {
  }

  public onMouseLeave() {
  }

  toggleDropdown(event?) {
    if (event) {
        const target = event.target || event.srcElement || event.currentTarget;
        const dropdown = target.offsetParent.nextElementSibling || target.nextElementSibling;

        dropdown.classList.toggle('show');
        this.openedDropdown = dropdown;
        window.addEventListener('mousedown', this.outsideClickHandler);
        window.addEventListener('dragstart', this.outsideClickHandler);
        this.openedDropdown.addEventListener('mousedown', this.insideClickHandler);
    } else {
        this.openedDropdown.classList.toggle('show');
        window.removeEventListener('mousedown', this.outsideClickHandler);
        window.removeEventListener('dragstart', this.outsideClickHandler);
        this.openedDropdown.removeEventListener('mousedown', this.insideClickHandler);
        this.openedDropdown = null;
    }
  }

  toggleAccordion(event) {
    const target = event.target || event.srcElement || event.currentTarget;
    const panel = target.nextElementSibling;
    target.classList.toggle('active');
    if (panel.style.display === 'block') {
        panel.style.display = 'none';
    } else {
        panel.style.display = 'block';
    }
  }

  private readonly outsideClickHandler = () => {
    this.toggleDropdown();
  }

  private readonly insideClickHandler = (e: MouseEvent) => {
    e.stopPropagation();
  }

  private sortChildren(node, orderBy: string, asc: boolean) {
    this.groups = null;
    const sortOrder = asc ? 1 : -1;
    const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
    if (orderBy) {
        switch (node.children[0].data.nodeType) {
            case NodeType.Record:
                this.groups = this.buildGroups(node, orderBy);
                this.groups.sort((a, b) => sortOrder * collator.compare(a[0].split(': ')[1], b[0].split(': ')[1]));
                break;
            case NodeType.Document:
                const alias = node.data.columns.find(c => c.name === orderBy).alias;
                // TODO: Consider making documents data.records the same as Record nodes have.
                //       Just need data.columnList and change data.records to an array of strings.
                const groups = node.children.reduce((acc, child) => {
                    (acc[`${alias}: ${child.data.record[orderBy]}`] = acc[`${alias}: ${child.data.record[orderBy]}`] || []).push(child);
                    return acc;
                }, {});
                this.groups = Object.entries(groups);
                this.groups.sort((a, b) => sortOrder * collator.compare(a[0].split(': ')[1], b[0].split(': ')[1]));
                break;
            default:
                let idx = node.children[0].data.columnList.indexOf(orderBy);
                if (idx <= 0) {
                    const column = node.data.columns.find(c => c.alias = orderBy);
                    idx = node.children[0].data.columnList.indexOf(column.name);
                }
                node.children.sort((a, b) => sortOrder * collator.compare(a.data.record[idx], b.data.record[idx]));
                break;
        }
    } else {
        node.children.sort((a, b) => sortOrder * collator.compare(a.name, b.name));
    }
    this.currentNode.data.asc = asc;
    this.currentNode.data.orderBy = orderBy;
  }
}
